/**
 * New Use Case Intake — core-dva-ai service (VITE_CORE_DVA_AI_URL)
 * POST /api/v1/new-use-case-intake/use-cases/from-standard-library
 * POST /api/v1/new-use-case-intake/use-cases/from-document
 */

import { calculateProfileStrength } from '@/utils/calculateProfileStrength';
import { auth, ensureAuthTenant } from '@/firebase';

const INTAKE_BASE = import.meta.env.VITE_CORE_DVA_AI_URL;

/** Default standard-library use case to clone when profile hits the threshold. */
export const DEFAULT_STANDARD_USE_CASE_ID = 15;

/** Organization Profile strength at which we auto-clone from the standard library. */
export const PROFILE_COMPLETION_CLONE_THRESHOLD = 80;

/** Outcomes returned by from-document for each source use case. */
export const FROM_DOCUMENT_OUTCOMES = {
  STAGED: 'staged',
  ALREADY_STAGED: 'already_staged',
  REJECTED: 'rejected',
  SKIPPED: 'skipped',
};

const storageKey = (clientId) =>
  `dva_std_library_clone_triggered_${String(clientId)}`;

async function getAuthToken() {
  ensureAuthTenant();
  const user = auth.currentUser;
  if (user) {
    return user.getIdToken(true);
  }
  return localStorage.getItem('dva_token');
}

/**
 * Turn an error payload field (string, object, or FastAPI validation array)
 * into readable text. Returns null when nothing usable is found.
 */
function toErrorText(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    const parts = value.map(toErrorText).filter(Boolean);
    return parts.length ? parts.join('; ') : null;
  }
  if (typeof value === 'object') {
    return (
      toErrorText(value.message) ||
      toErrorText(value.msg) ||
      toErrorText(value.detail) ||
      toErrorText(value.error) ||
      toErrorText(value.reason) ||
      null
    );
  }
  return String(value);
}

function getErrorMessage(json, fallback) {
  return (
    toErrorText(json?.message) ||
    toErrorText(json?.detail) ||
    toErrorText(json?.error) ||
    fallback
  );
}

async function buildIntakeHeaders(clientId) {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Missing auth token — cannot call use-case intake');
  }
  if (!clientId) {
    throw new Error('Missing tenant id (client_id) — cannot call use-case intake');
  }

  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-tenant-id': String(clientId),
  };

  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    headers['x-correlation-id'] = crypto.randomUUID();
  }

  return headers;
}

/**
 * Normalize from-document response into a flat list of per-source outcomes.
 * Accepts common shapes: { outcomes: [] }, { results: [] }, { data: [] }, or [].
 */
export function normalizeFromDocumentOutcomes(response) {
  const rawList = Array.isArray(response)
    ? response
    : Array.isArray(response?.outcomes)
      ? response.outcomes
      : Array.isArray(response?.results)
        ? response.results
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.items)
            ? response.items
            : [];

  return rawList.map((item) => {
    const outcome = String(
      item?.outcome || item?.status || item?.result || '',
    ).toLowerCase();
    const sourceId =
      item?.source_use_case_id ??
      item?.sourceUseCaseId ??
      item?.use_case_id ??
      item?.useCaseId ??
      null;
    const numericSource =
      sourceId === null || sourceId === undefined || sourceId === ''
        ? null
        : Number(sourceId);

    return {
      source_use_case_id: Number.isFinite(numericSource) ? numericSource : null,
      outcome,
      candidate_id:
        item?.candidate_id ??
        item?.candidateId ??
        item?.staged_candidate_id ??
        null,
      reason:
        toErrorText(item?.reason) ||
        toErrorText(item?.message) ||
        toErrorText(item?.detail) ||
        null,
      raw: item,
    };
  });
}

/**
 * Find the outcome for a specific source use case id.
 */
export function findFromDocumentOutcome(response, sourceUseCaseId) {
  const outcomes = normalizeFromDocumentOutcomes(response);
  const target = Number(sourceUseCaseId);
  const match = outcomes.find(
    (o) => o.source_use_case_id !== null && o.source_use_case_id === target,
  );
  if (match) return match;
  // Single-item approve: if API returns one outcome without id, use it
  if (outcomes.length === 1 && outcomes[0].source_use_case_id === null) {
    return {
      ...outcomes[0],
      source_use_case_id: Number.isFinite(target) ? target : null,
    };
  }
  return null;
}

export function isSuccessfulFromDocumentOutcome(outcome) {
  const value = String(outcome || '').toLowerCase();
  return (
    value === FROM_DOCUMENT_OUTCOMES.STAGED ||
    value === FROM_DOCUMENT_OUTCOMES.ALREADY_STAGED
  );
}

/**
 * Clone a pre-vetted use case from the standard library into the tenant.
 */
export async function cloneUseCaseFromStandardLibrary({
  standardUseCaseId = DEFAULT_STANDARD_USE_CASE_ID,
  triggerProfileGeneration = true,
  clientId,
} = {}) {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Missing auth token — cannot clone from standard library');
  }

  const headers = clientId
    ? await buildIntakeHeaders(clientId)
    : {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

  const response = await fetch(
    `${INTAKE_BASE}/api/v1/new-use-case-intake/use-cases/from-standard-library`,
    {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        standard_use_case_id: Number(standardUseCaseId),
        trigger_profile_generation: Boolean(triggerProfileGeneration),
      }),
    },
  );

  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      getErrorMessage(json, `Standard library clone failed (${response.status})`),
    );
  }
  return json;
}

/**
 * Promote approved document-sourced use cases into the tenant via Core DVA AI.
 * Returns the raw API payload (includes per-source outcomes).
 */
export async function createUseCasesFromDocument({
  sourceUseCaseIds,
  clientId,
  tenantId,
  autoPromote = false,
} = {}) {
  const resolvedClientId = clientId || tenantId;
  if (!resolvedClientId) {
    throw new Error('Missing tenant (client) id');
  }

  const ids = (Array.isArray(sourceUseCaseIds) ? sourceUseCaseIds : [])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id));

  if (!ids.length) {
    throw new Error('At least one source_use_case_id is required');
  }

  const response = await fetch(
    `${INTAKE_BASE}/api/v1/new-use-case-intake/use-cases/from-document`,
    {
      method: 'POST',
      headers: await buildIntakeHeaders(resolvedClientId),
      credentials: 'include',
      body: JSON.stringify({
        source_use_case_ids: ids,
        auto_promote: Boolean(autoPromote),
      }),
    },
  );

  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      getErrorMessage(
        json,
        `Approval failed (${response.status}). Please try again.`,
      ),
    );
  }
  return json;
}

/**
 * Promote staged candidates into live use cases. On success the API kicks off
 * measure generation, scoring, profile generation and ROI for each promoted use case.
 * Response: { batch_id, promoted_count, promotions: [{ candidate_id, use_case_id }],
 *             downstream_triggered_for: [use_case_id], execution_time_ms }
 */
export async function batchPromoteCandidates({ candidateIds, clientId, batchId } = {}) {
  if (!clientId) {
    throw new Error('Missing tenant (client) id');
  }

  const ids = (Array.isArray(candidateIds) ? candidateIds : [])
    .map((id) => String(id ?? '').trim())
    .filter(Boolean);

  if (!ids.length) {
    throw new Error('At least one candidate_id is required');
  }

  const body = { candidate_ids: ids };
  if (batchId) body.batch_id = String(batchId);

  const response = await fetch(
    `${INTAKE_BASE}/api/v1/new-use-case-intake/candidates/batch-promote`,
    {
      method: 'POST',
      headers: await buildIntakeHeaders(clientId),
      credentials: 'include',
      body: JSON.stringify(body),
    },
  );

  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      getErrorMessage(json, `Ingestion failed (${response.status}). Please try again.`),
    );
  }
  return json;
}

/**
 * When Organization Profile strength first reaches the threshold, clone once
 * from the standard library (idempotent per client via localStorage).
 */
export async function maybeCloneFromStandardLibraryOnProfileThreshold(
  clientId,
  profile,
) {
  if (!clientId || !profile) {
    return { triggered: false, score: 0 };
  }

  const { score } = calculateProfileStrength(profile);
  if (!Number.isFinite(score) || score < PROFILE_COMPLETION_CLONE_THRESHOLD) {
    return { triggered: false, score: Number(score) || 0 };
  }

  const key = storageKey(clientId);
  if (typeof localStorage !== 'undefined' && localStorage.getItem(key)) {
    return { triggered: false, score };
  }

  try {
    const result = await cloneUseCaseFromStandardLibrary({
      standardUseCaseId: DEFAULT_STANDARD_USE_CASE_ID,
      triggerProfileGeneration: true,
      clientId,
    });
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(key, new Date().toISOString());
    }
    console.info(
      '[useCaseIntake] Cloned standard use case after profile reached',
      score,
      '%',
      result,
    );
    return { triggered: true, score, result };
  } catch (error) {
    console.error(
      '[useCaseIntake] Failed to clone from standard library at profile threshold:',
      error,
    );
    return { triggered: false, score, error };
  }
}
